import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Play, X, Star, ArrowRight, Trophy, TrendingUp, MapPin,
  Phone, Quote, ChevronRight, Users, Target, Shield, Lock,
} from "lucide-react";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

const VIDEO_CASE_STUDIES = [
  {
    name: "Bianca",
    industry: "Bail Bonds, Florida",
    headline: "3X'd Google Maps Visibility in Under 4 Months",
    result: "200% Maps Visibility Surge",
    vimeoId: "1143295236",
    thumbnail: "https://i.vimeocdn.com/video/2091427506-bd802fc06dc2bfe29d4272a7a59791ec41fa243fa1cce08ddd0116a730708eb1-d?mw=600&q=85",
    description: "Bianca runs one of Florida’s biggest bail bond companies and plugged into Maps Domination to own the state, county by county—cranking up Monroe, eyeing Polk next, and rolling the extra cash into new locations so she can build a legacy business for her daughter.",
  },
  {
    name: "Mark",
    industry: "Eyeglasses, Pensacola",
    headline: "Map Pack Monopolization in 7 Weeks",
    result: "700% Keyword Growth",
    vimeoId: "1140420130",
    thumbnail: "https://i.vimeocdn.com/video/2087431083-825dae075099bcd43f55813e0be9c8f92f33d5cee066caa7ae3db3b6e729b5f7-d?mw=600&q=85",
    description: "Mark went from “just another eye doc business” to the 2,400-pound gorilla of the Gulf Coast—dominating search, packing his schedule. In under 3 months, he now owns 100% of the Google Map Pack for eye exam keywords (all 3 spots) with wall-to-wall visibility. He’s now closing on his 4th location.",
  },
  {
    name: "Josh",
    industry: "Family-Owned Business",
    headline: "From Invisible to Selling to Private Equity",
    result: "Top 3 in 8 Weeks",
    vimeoId: "1139093793",
    thumbnail: "https://i.vimeocdn.com/video/2085448257-662dda97c6d3d849a0c021ab3f1f240fff5a965261be5e3c24c509efea7a1782-d?mw=600&q=85",
    description: "Josh wasn’t in the top 3 on Google Maps—he didn’t exist. Digital Domination ranked him in the Top 3 and flooded him with new customers in less than 8 weeks. The transformation was so dramatic, he ended up selling his company to private equity.",
  },
  {
    name: "Simply Health",
    industry: "Medical Center",
    headline: "From Unpredictable Calls to Daily Patient Growth",
    result: "Consistent Patient Growth",
    vimeoId: "1143008329",
    thumbnail: "https://i.vimeocdn.com/video/2091032070-a926df1ea944d0575f1a434f754968f54b08b0ee4d423f422fd6da4c9c8ec150-d?mw=600&q=85",
    description: "Simply Health went from unpredictable calls to consistent daily patient growth. By winning the map and owning the moment, they turned search intent into booked appointments—reliably, every single day.",
  },
  {
    name: "Medina Auto Group",
    industry: "Car Dealership, 4 Locations",
    headline: "Deleted 89 Unfair 1-Star Reviews & Took Over Google Maps",
    result: "Dominating Every Search",
    vimeoId: "1144736149",
    thumbnail: "https://i.vimeocdn.com/video/2093428261-67f4e90e47562cf621b048d1f328ecaabad0b72d841f9ce8272e0fae1bf6bbd8-d?mw=600&q=85",
    description: "Medina Auto Group was buried under 89 unfair 1-star reviews. Digital Domination wiped them out and took over Google Maps for every major buyer search across all 4 rooftops—in record time.",
  },
  {
    name: "California HVAC Company",
    industry: "HVAC Contractor, California",
    headline: "$746,635 in a Single Month — 223% YoY Revenue Surge",
    result: "$746K Single Month",
    vimeoId: "1143003880",
    thumbnail: "https://i.vimeocdn.com/video/2091025391-15323566482f4d4e972039b12c1a672519f26b193ad41d68d3629d5b6568d62c-d?mw=600&q=85",
    description: "This California HVAC company was struggling for leads. After plugging into Digital Domination’s advanced Google Maps optimization, they hit $746,635 in a single month—a 223.74% year-over-year revenue surge.",
  },
];

const YOUTUBE_TESTIMONIALS = [
  { title: "I Didn’t Believe It… Then 90 Days Later THIS Happened", videoId: "xHM3-d6urk8" },
  { title: "Maps Domination Review — Dino’s Barbershop Testimonial", videoId: "d3OavM_J1ls" },
  { title: "90-Day Maps Domination Changed Everything — Real Business Owner", videoId: "geHfTf4yMKA" },
  { title: "Digital Domination Review — Jewelry Store Testimonial", videoId: "pMhaUROjFAk" },
  { title: "Testimonial Review — Las Vegas Family Owned Business", videoId: "Mh-3Dl10-p4" },
  { title: "Real Business Owners Share Their Maps Domination Results", videoId: "A5aDK8hMFco" },
];

const CLIENT_WINS = [
  {
    name: "Alex",
    industry: "Barbershop, Las Vegas",
    result: "640% Market Share Increase",
    image: "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-7-scaled.webp",
    quote: "In 5 months with Maps Domination, Alex’s new Las Vegas barbershop went from “who are you?” to a 640% increase in market share—owning the map, stealing market share from top competitors who had been in business for decades. His shop is booked solid, and he’s now rolling profits into a second location.",
  },
  {
    name: "Daniel",
    industry: "Local Business, Nashville",
    result: "Phones Won’t Stop Ringing",
    image: "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-4-scaled.webp",
    quote: "Daniel plugged into Maps Domination and felt it working instantly—5 campaign win updates in the first 48 hours. Within 2 months, the phones wouldn’t stop ringing, he’s hiring more staff, and now he’s scouting new locations to handle the demand.",
  },
  {
    name: "Phoenix Auto Repair",
    industry: "Auto Repair, Phoenix",
    result: "+400% Market Share in 1 Week",
    image: "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-6-scaled.webp",
    quote: "Phoenix Auto Repair was buried under competitors. Great shop, loyal customers… but the map pack didn’t care. +400% jump in market share in a single week and the owner said “I absolutely love domination!”",
  },
  {
    name: "John Roberts",
    industry: "Mobile Mechanic",
    result: "0% → 37% Market Share in 4 Weeks",
    image: "https://digitaldomination.ai/wp-content/uploads/2025/09/Trevor.jpg",
    quote: "“I didn’t trust you at first… Oh, you know I trust you. I’m not going nowhere.” In only 4 weeks, idle trucks went back on the road, his crew schedule filled up, and the business regained real market share on Google Maps.",
  },
];

const SCREENSHOT_WINS = [
  "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-9-scaled.webp",
  "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-10-scaled.webp",
  "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-sm3-1.png",
  "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-sm2-1.png",
  "https://digitaldomination.ai/wp-content/uploads/2025/10/Client-Feedback-sm4-1.png",
];

const GEO_SCREENSHOTS = [
  "https://digitaldomination.ai/wp-content/uploads/2025/11/Add-SS6-scaled.jpg",
  "https://digitaldomination.ai/wp-content/uploads/2025/11/Add-SS2-scaled.jpg",
  "https://digitaldomination.ai/wp-content/uploads/2025/11/ss-8.png",
  "https://digitaldomination.ai/wp-content/uploads/2025/11/Add-SS3.png",
  "https://digitaldomination.ai/wp-content/uploads/2025/11/Add-SS4.png",
  "https://digitaldomination.ai/wp-content/uploads/2025/11/Add-SS5.png",
];

function VideoModal({ vimeoId, youtubeId, onClose }: { vimeoId?: string; youtubeId?: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const src = vimeoId
    ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`
    : `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="video-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative w-full max-w-4xl mx-4 aspect-video">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors" data-testid="button-close-video-modal">
          <X className="w-6 h-6" />
        </button>
        <iframe src={src} className="w-full h-full rounded-xl" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      </div>
    </div>
  );
}

function ImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="image-lightbox">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative max-w-5xl mx-4">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors">
          <X className="w-6 h-6" />
        </button>
        <img src={src} alt="Client result" className="w-full rounded-xl" />
      </div>
    </div>
  );
}

export default function TestimonialsPage() {
  const [activeVimeo, setActiveVimeo] = useState<string | null>(null);
  const [activeYoutube, setActiveYoutube] = useState<string | null>(null);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [feedbackFailed, setFeedbackFailed] = useState(0);
  const [geoFailed, setGeoFailed] = useState(0);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      {activeVimeo && <VideoModal vimeoId={activeVimeo} onClose={() => setActiveVimeo(null)} />}
      {activeYoutube && <VideoModal youtubeId={activeYoutube} onClose={() => setActiveYoutube(null)} />}
      {lightboxImg && <ImageLightbox src={lightboxImg} onClose={() => setLightboxImg(null)} />}

      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0A0A0F]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-40 flex items-center justify-between">
          <Link href="/">
            <img src={logoBlack} alt="Tork" className="h-36 cursor-pointer block dark:hidden" data-testid="logo-testimonials-nav" />
            <img src={logoWhite} alt="Tork" className="h-36 cursor-pointer hidden dark:block" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/"><span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Home</span></Link>
            <Link href="/testimonials"><span className="text-sm text-white font-medium cursor-pointer">Testimonials</span></Link>
            <Link href="/login"><span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Login</span></Link>
            <Link href="/login">
              <Button className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-9 px-5" data-testid="nav-cta-trial">Start Free Trial</Button>
            </Link>
          </div>
          <div className="md:hidden">
            <Link href="/login">
              <Button className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-9 px-4">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-16 px-6 relative overflow-hidden" data-testid="section-testimonials-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#F6642A]/8 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F6642A]/20 bg-[#F6642A]/5 text-sm text-[#F6642A] font-medium mb-6">
            <Trophy className="w-4 h-4" />
            Real Results. Real Wins. Real Value.
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 tracking-tight" data-testid="text-testimonials-h1">
            See How Thousands of Local Businesses Have Dominated Google Maps With{" "}
            <span className="text-[#F6642A]">Digital Domination</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
            See how real local businesses are crushing their competition, flooding their phones with calls, and scaling to new locations with Digital Domination.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#F6642A]" /> 5,000+ businesses served</span>
            <span className="w-1 h-1 rounded-full bg-gray-600 hidden sm:block" />
            <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 5.0 average rating</span>
            <span className="w-1 h-1 rounded-full bg-gray-600 hidden sm:block" />
            <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-green-400" /> 7-day trial for $1</span>
          </div>
        </div>
      </section>

      <section className="py-16 px-6" data-testid="section-video-case-studies">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Play className="w-5 h-5 text-[#F6642A]" />
            <h2 className="text-2xl font-bold text-white" data-testid="text-video-studies-heading">Video Case Studies</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {VIDEO_CASE_STUDIES.map((cs, i) => (
              <div key={i} className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden hover:border-[#F6642A]/30 transition-colors" data-testid={`card-case-study-${i}`}>
                <div
                  className="relative aspect-video cursor-pointer group"
                  onClick={() => setActiveVimeo(cs.vimeoId)}
                >
                  <img src={cs.thumbnail} alt={cs.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#F6642A] flex items-center justify-center shadow-lg shadow-[#F6642A]/30 group-hover:scale-110 transition-transform">
                      <Play className="w-7 h-7 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="text-[11px] font-bold bg-[#F6642A] text-white px-2.5 py-1 rounded-full">{cs.result}</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-white mb-1">{cs.headline}</h3>
                  <p className="text-xs text-[#F6642A] font-semibold mb-3">{cs.name} &mdash; {cs.industry}</p>
                  <p className="text-sm text-gray-400 leading-relaxed">{cs.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-white/[0.01]" data-testid="section-client-wins">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <h2 className="text-2xl font-bold text-white" data-testid="text-client-wins-heading">Client Wins & Reviews</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CLIENT_WINS.map((w, i) => (
              <div key={i} className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden" data-testid={`card-client-win-${i}`}>
                <img
                  src={w.image}
                  alt={w.name}
                  className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => setLightboxImg(w.image)}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-[#F6642A]/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#F6642A]">{w.name[0]}</span>
                    </div>
                    <div>
                      <span className="text-sm font-bold text-white">{w.name}</span>
                      <span className="text-xs text-gray-500 block">{w.industry}</span>
                    </div>
                    <span className="ml-auto text-xs font-semibold text-[#F6642A] bg-[#F6642A]/10 px-2.5 py-1 rounded-full">{w.result}</span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{w.quote}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {feedbackFailed < SCREENSHOT_WINS.length && (
      <section className="py-16 px-6" data-testid="section-screenshot-proof">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Quote className="w-5 h-5 text-[#F6642A]" />
            <h2 className="text-2xl font-bold text-white" data-testid="text-more-reviews-heading">More Client Feedback</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCREENSHOT_WINS.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Client feedback ${i + 1}`}
                className="w-full rounded-xl border border-[#1E1E2E] cursor-pointer hover:border-[#F6642A]/30 transition-colors"
                onClick={() => setLightboxImg(src)}
                onError={(e) => { e.currentTarget.style.display = "none"; setFeedbackFailed((c) => c + 1); }}
                data-testid={`img-feedback-${i}`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {geoFailed < GEO_SCREENSHOTS.length && (
      <section className="py-16 px-6 bg-white/[0.01]" data-testid="section-geogrid-proof">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <h2 className="text-2xl font-bold text-white" data-testid="text-geogrid-proof-heading">GeoGrid Proof &mdash; Red to Green Flips</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {GEO_SCREENSHOTS.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`GeoGrid result ${i + 1}`}
                className="w-full rounded-xl border border-[#1E1E2E] cursor-pointer hover:border-green-500/30 transition-colors"
                onClick={() => setLightboxImg(src)}
                onError={(e) => { e.currentTarget.style.display = "none"; setGeoFailed((c) => c + 1); }}
                data-testid={`img-geogrid-${i}`}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="py-16 px-6" data-testid="section-youtube-testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Play className="w-5 h-5 text-red-500" />
            <h2 className="text-2xl font-bold text-white" data-testid="text-youtube-heading">More Client Testimonials</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {YOUTUBE_TESTIMONIALS.map((yt, i) => (
              <div
                key={i}
                className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden hover:border-red-500/30 transition-colors cursor-pointer group"
                onClick={() => setActiveYoutube(yt.videoId)}
                data-testid={`card-youtube-${i}`}
              >
                <div className="relative aspect-video">
                  <img
                    src={`https://img.youtube.com/vi/${yt.videoId}/hqdefault.jpg`}
                    alt={yt.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm font-medium text-white line-clamp-2">{yt.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-white/[0.01]" data-testid="section-case-study-featured">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl bg-[#12121A] border border-[#1E1E2E] overflow-hidden">
            <div className="p-8 md:p-10">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F6642A]">FEATURED CASE STUDY</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-2" data-testid="text-featured-case-study">
                From Invisible to 37% Market Share in 4 Weeks
              </h2>
              <p className="text-gray-400 mb-6">John Roberts, Mobile Mechanic</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Market Share", value: "0% → 37%", sub: "in 28 days" },
                  { label: "Coverage", value: "Red → Green", sub: "Map Pack wins multiplied" },
                  { label: "Pipeline", value: "Way more calls", sub: "Calendar all booked" },
                  { label: "Trust Shift", value: "“I’m staying”", sub: "From skeptic to referrer" },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="text-lg font-bold text-[#F6642A]">{s.value}</div>
                    <div className="text-xs font-semibold text-white/60 mt-1">{s.label}</div>
                    <div className="text-[11px] text-white/30 mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><Target className="w-4 h-4 text-[#F6642A]" /> The Maps Domination Protocol</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { icon: MapPin, label: "Local Proximity Amplification™", desc: "Expand how far John shows up on Google Maps." },
                    { icon: Target, label: "Geographic Signal Optimization™", desc: "Strengthen trust and authority signals." },
                    { icon: Lock, label: "Geolock Defense Matrix™", desc: "Lock rankings long-term against competitors." },
                  ].map((p, i) => (
                    <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                      <p.icon className="w-4 h-4 text-[#F6642A] mb-2" />
                      <div className="text-xs font-bold text-white mb-1">{p.label}</div>
                      <div className="text-[11px] text-gray-500">{p.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-5 rounded-xl bg-[#F6642A]/5 border border-[#F6642A]/20">
                <Quote className="w-5 h-5 text-[#F6642A]/40 mb-2" />
                <p className="text-sm text-gray-300 italic leading-relaxed">
                  &ldquo;I did not trust you&hellip; would you say you trust us now?&rdquo; &mdash; &ldquo;Oh you know I trust you. I&rsquo;m not going nowhere.&rdquo;
                </p>
                <p className="text-sm text-gray-300 italic leading-relaxed mt-2">
                  &ldquo;It&rsquo;s going fast&hellip; it has increased business. I&rsquo;d stay&hellip; I&rsquo;d refer you. I&rsquo;ve been telling my buddy.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 relative" data-testid="section-testimonials-cta">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-[#F6642A]/8 via-transparent to-transparent" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">See Your Own Red-to-Green Flip</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Run a free Maps visibility scan and see where you stand. If you qualify, we&rsquo;ll set week-12 market share domination goals and back it with our guarantee.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/login">
              <Button className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-13 px-10 text-base" data-testid="cta-testimonials-scan">
                Run a Free Maps Scan
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-4">Best case you dominate. Worst case you keep the free plan.</p>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src={logoBlack} alt="Tork" className="h-32 block dark:hidden" />
          <img src={logoWhite} alt="Tork" className="h-32 hidden dark:block" />
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link href="/"><span className="hover:text-white transition-colors cursor-pointer">Home</span></Link>
            <Link href="/login"><span className="hover:text-white transition-colors cursor-pointer">Login</span></Link>
          </div>
          <p className="text-xs text-gray-600">&copy; 2026 Digital Domination. 5,000+ businesses helped.</p>
        </div>
      </footer>
    </div>
  );
}
